import { AlertTriangleIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import type { CobrancaDoVoucher, ResumoDoVoucher } from '../services/specialContractsApi'
import { formatMoney } from '../utils/contractBilling'
import { rotuloDaCompetencia } from '../utils/format'
import {
  ROTULO_DA_LIBERACAO,
  ROTULO_DO_ACUMULO,
  descontoDoVoucher,
  formatarDiaDoVoucher,
  formatarViradaDoVoucher,
  type TermosDoVoucherGravados,
} from '../utils/voucher'

/**
 * #1107 — o contrato com voucher como a revenda (RS) e o cliente (EC) o veem: os termos (crédito,
 * valor faturado, desconto conferível, acúmulo, liberação, quitação atrasada), o saldo do voucher
 * com a próxima virada, o aviso de inadimplência e as cobranças por competência. Uma cópia só,
 * para os dois portais dizerem a mesma coisa sobre o mesmo contrato.
 */

const dinheiro = (v: string | null | undefined, currency: string) =>
  v === null || v === undefined ? '—' : formatMoney(Number(v), currency)

const Secao = ({ titulo, children }: { titulo: string; children: ReactNode }) => (
  <section className="space-y-2">
    <h3 className="text-sm font-semibold text-gray-900">{titulo}</h3>
    {children}
  </section>
)

const Linha = ({ rotulo, children }: { rotulo: string; children: ReactNode }) => (
  <div className="flex justify-between gap-3">
    <dt className="text-gray-500">{rotulo}</dt>
    <dd className="text-right text-gray-900">{children}</dd>
  </div>
)

/** Situação de uma cobrança P do voucher, sem inventar: financiada, paga, vencida ou em aberto. */
export function situacaoDaCobrancaDoVoucher(c: CobrancaDoVoucher, agora: number): string {
  if (c.financiada_por) {
    return `Financiada pela quitação de ${rotuloDaCompetencia(c.financiada_por)}`
  }
  if (c.pago) return 'Paga'
  // não paga e não mais cobrável: nem "vencida" nem "em aberto"
  if (c.status === 'cancelled') return 'Cancelada'
  if (c.status === 'refunded') return 'Estornada'
  return new Date(c.vencimento).getTime() < agora ? 'Vencida' : 'Em aberto'
}

function rotuloDaLiberacao(t: TermosDoVoucherGravados): string {
  if (t.voucher_release_policy === 'at_start_with_grace') {
    // sem a tolerância gravada, a falta aparece — nunca um "0 dias" inventado
    return t.voucher_grace_days == null
      ? 'Na abertura, com tolerância de — dias'
      : `Na abertura, com tolerância de ${t.voucher_grace_days} dias`
  }
  return t.voucher_release_policy ? ROTULO_DA_LIBERACAO[t.voucher_release_policy] : '—'
}

export const PainelDoVoucher = ({
  termos: t,
  resumo: v,
  currency,
  ativo,
}: {
  termos: TermosDoVoucherGravados
  resumo: ResumoDoVoucher | undefined
  currency: string
  /** contrato ativo: só ele tem próxima virada */
  ativo: boolean
}) => {
  const desconto = descontoDoVoucher(t.voucher_credit_amount, t.voucher_price)
  const agora = Date.now()
  return (
    <div className="space-y-5">
      <Secao titulo="Termos do voucher">
        <dl className="space-y-1 text-sm" data-testid="termos-do-voucher">
          <Linha rotulo="Crédito mensal para consumo">
            {dinheiro(t.voucher_credit_amount, currency)}
          </Linha>
          <Linha rotulo="Valor mensal deste contrato">{dinheiro(t.voucher_price, currency)}</Linha>
          <Linha rotulo="Desconto">
            <span data-testid="desconto-do-voucher">
              {desconto
                ? `${formatMoney(desconto.valor, currency)} (${desconto.percentual.toLocaleString('pt-BR')}%)`
                : '—'}
            </span>
          </Linha>
          <Linha rotulo="Saldo não usado na virada">
            {t.voucher_rollover ? ROTULO_DO_ACUMULO[t.voucher_rollover] : '—'}
          </Linha>
          <Linha rotulo="Liberação do crédito">{rotuloDaLiberacao(t)}</Linha>
          <Linha rotulo="Pagamento atrasado">
            {t.voucher_late_payment_carry
              ? 'Aproveitado na competência vigente'
              : 'Não aproveitado na competência vigente'}
          </Linha>
        </dl>
      </Secao>
      {v && (
        <>
          {v.inadimplente && (
            <div
              className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800"
              data-testid="voucher-inadimplente"
            >
              <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
              Há cobrança do voucher em atraso: o saldo não usado fica congelado e novas
              competências não abrem até a regularização.
            </div>
          )}
          <Secao titulo="Saldo do voucher">
            <dl className="space-y-1 text-sm" data-testid="saldo-do-voucher">
              <Linha rotulo="Disponível">{dinheiro(v.saldo_disponivel, currency)}</Linha>
              <Linha rotulo="Congelado">{dinheiro(v.saldo_congelado, currency)}</Linha>
              <Linha rotulo={`Consumido em ${rotuloDaCompetencia(v.competencia_atual)}`}>
                {dinheiro(v.consumido_na_competencia, currency)}
              </Linha>
              <Linha rotulo="Concedido no contrato">{dinheiro(v.concedido_total, currency)}</Linha>
              <Linha rotulo="Consumido no contrato">{dinheiro(v.consumido_total, currency)}</Linha>
              {ativo && (
                <Linha rotulo="Próxima virada">{formatarViradaDoVoucher(v.proxima_virada)}</Linha>
              )}
            </dl>
          </Secao>
          <Secao titulo="Cobranças do voucher">
            {v.cobrancas.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhuma cobrança ainda.</p>
            ) : (
              <ul className="divide-y divide-gray-100 text-sm" data-testid="cobrancas-do-voucher">
                {v.cobrancas.map((c) => (
                  <li key={c.competencia} className="space-y-0.5 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900">
                        {rotuloDaCompetencia(c.competencia)}
                      </span>
                      <span className="font-semibold text-gray-900">
                        {dinheiro(c.valor, currency)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {situacaoDaCobrancaDoVoucher(c, agora)}
                      {!c.financiada_por && ` · vence ${formatarDiaDoVoucher(c.vencimento)}`}
                      {c.credito_liberado
                        ? ' · Crédito liberado'
                        : // cancelada/estornada não vai ser paga: nada de "aguardando"
                          c.status !== 'cancelled' && c.status !== 'refunded'
                          ? ' · Crédito aguardando o pagamento'
                          : ''}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Secao>
        </>
      )}
    </div>
  )
}
